import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@styles/theme';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { logout } from '@store/features/userSlice';
import { clearAuthCookies } from '@utils/cookie';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background.secondary};
`;

const Sidebar = styled.aside<{ isCollapsed: boolean }>`
  width: ${(props) => (props.isCollapsed ? '80px' : '260px')};
  background-color: ${theme.colors.background.default};
  border-right: 1px solid ${theme.colors.border.default};
  transition: width ${theme.transitions.normal};
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: ${theme.zIndex.fixed};

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: ${(props) => (props.isCollapsed ? '64px' : '260px')};
  }
`;

const SidebarHeader = styled.div<{ $collapsed: boolean }>`
  padding: ${(props) => props.$collapsed ? theme.spacing.sm : theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.default};
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$collapsed ? 'center' : 'space-between'};
`;

const Logo = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  white-space: nowrap;
  overflow: hidden;
`;

const ToggleButton = styled.button`
  min-width: 44px;
  min-height: 44px;
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.md};
  transition: background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background.secondary};
  }
`;

const Nav = styled.nav`
  padding: ${theme.spacing.md} 0;
`;

const NavItem = styled(Link)<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  color: ${(props) =>
    props.isActive ? theme.colors.primary : theme.colors.text.primary};
  text-decoration: none;
  font-weight: ${(props) =>
    props.isActive
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.normal};
  background-color: ${(props) =>
    props.isActive ? theme.colors.background.secondary : 'transparent'};
  border-left: 3px solid
    ${(props) => (props.isActive ? theme.colors.primary : 'transparent')};
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background.secondary};
    color: ${theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md};
  }
`;

const NavIcon = styled.span`
  font-size: ${theme.typography.fontSize.xl};
  width: 24px;
  text-align: center;
`;

const MainContent = styled.main<{ sidebarWidth: string }>`
  margin-left: ${(props) => props.sidebarWidth};
  flex: 1;
  min-width: 0;
  transition: margin-left ${theme.transitions.normal};

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-left: 64px;
  }
`;

const TopBar = styled.header`
  background-color: ${theme.colors.background.default};
  border-bottom: 1px solid ${theme.colors.border.default};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: ${theme.zIndex.sticky};
  box-shadow: ${theme.shadows.sm};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md};
    gap: ${theme.spacing.sm};
    h2 { font-size: ${theme.typography.fontSize.headline}; }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.sm};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ContentArea = styled.div`
  padding: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md};
  }
`;

export const AdminLayout: React.FC = () => {
  const { t } = useTranslation('admin');
  const [isCollapsed, setIsCollapsed] = useState(() => window.matchMedia(`(max-width: ${theme.breakpoints.tablet})`).matches);
  const location = useLocation();
  const { currentUser } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${theme.breakpoints.tablet})`);
    const collapseOnSmallScreen = () => {
      if (media.matches) setIsCollapsed(true);
    };
    media.addEventListener('change', collapseOnSmallScreen);
    return () => media.removeEventListener('change', collapseOnSmallScreen);
  }, []);

  const menuItems = [
    { path: '/admin/dashboard', label: t('dashboard.title'), icon: '◫' },
    { path: '/admin/products', label: t('products'), icon: '📦' },
    { path: '/admin/images', label: t('title', { ns: 'images' }), icon: '▧' },
    { path: '/admin/categories', label: t('categories'), icon: '🏷️' },
    { path: '/admin/orders', label: t('orders'), icon: '📋' },
    { path: '/admin/coupons', label: t('couponsMenu'), icon: '🎟️' },
    { path: '/admin/traffic', label: t('trafficMenu'), icon: '📊' },
    { path: '/admin/consultations', label: t('title', { ns: 'consultation' }), icon: '✉' },
    { path: '/admin/announcement', label: t('announcementMenu'), icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M4 9h4l11-4v14L8 15H4V9Z" />
        <path d="m8 15 2 5H6l-2-5M8 9v6" />
      </svg>
    ) },
    { path: '/admin/comments', label: t('commentsMenu'), icon: '💬' },
  ];

  const handleLogout = () => {
    clearAuthCookies();
    dispatch(logout());
    navigate('/admin/login');
  };

  return (
    <LayoutContainer>
      <Sidebar isCollapsed={isCollapsed}>
        <SidebarHeader $collapsed={isCollapsed}>
          {!isCollapsed && <Logo>{t('adminTitle')}</Logo>}
          <ToggleButton aria-label={t(isCollapsed ? 'expandSidebar' : 'collapseSidebar')}
            aria-expanded={!isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)}>
            {isCollapsed ? '→' : '←'}
          </ToggleButton>
        </SidebarHeader>
        <Nav>
          {menuItems.map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              isActive={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
              aria-label={item.label}
              title={item.label}
            >
              <NavIcon>{item.icon}</NavIcon>
              {!isCollapsed && <span>{item.label}</span>}
            </NavItem>
          ))}
        </Nav>
      </Sidebar>
      <MainContent sidebarWidth={isCollapsed ? '80px' : '260px'}>
        <TopBar>
          <h2>{t('adminBackend')}</h2>
          <UserInfo>
            <span>{currentUser?.name || t('adminUser')}</span>
            <button onClick={handleLogout}>{t('logout', { ns: 'common' })}</button>
          </UserInfo>
        </TopBar>
        <ContentArea>
          <Outlet />
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};
